// Permission constants — mirror DocAI portal pattern.
// Use these everywhere we gate Routes / sidebar items / buttons via
// checkUserURLMethodAccess(module, method, actionUrl).
//
// IMPORTANT (Role & Access Management PRD, Phase 2): module names and
// action URLs below are the SINGLE SHARED CONTRACT with the backend
// permission catalog (sulsa_schema.module_actions, seeded by V49).
//   * `moduleName.*` values match `modules.name` in the DB exactly.
//   * `actionUrl.*` values are RELATIVE REST paths (no /api/v1 prefix)
//     matching `module_actions.action_url` exactly.
// A mismatch silently denies access under server-driven gating, so keep
// these in lockstep with the migration. Entries marked "FE-only" have no
// backend endpoint (route grouping / view-only screens) and resolve only
// via the static fallback below — they are not in the catalog.

export const moduleName = {
  // FE-only route groupings / view screens (not in the catalog)
  DASHBOARD: "DASHBOARD",
  INVENTORY: "INVENTORY",
  TRANSFER: "TRANSFER",
  SETTING: "SETTING",
  PROFILE: "PROFILE",
  MASTER_TYPE: "MASTER_TYPE",
  FILE: "FILE",
  // Catalog-backed modules — values match modules.name in the DB
  POS: "POS",
  CUSTOMER: "CUSTOMERS",
  STORE: "STORES",
  USER: "USERS",
  ROLE: "ROLES",
  GST: "GST",
  INVOICE_SETTINGS: "INVOICE_SETTINGS",
  TAX_SETTINGS: "TAX_SETTINGS",
  STORE_GST_SLABS: "STORE_GST_SLABS",
  PRODUCTS: "PRODUCTS",
  PRODUCT_SYNC: "PRODUCT_SYNC",
  STOCK: "STOCK",
  GRN: "GRN",
  REGISTER: "REGISTERS",
  COMPANY: "COMPANY",
  DISCOUNT: "DISCOUNT",
  MEMBERSHIP: "MEMBERSHIP",
  LOYALTY: "LOYALTY",
  CREDIT_NOTE: "CREDIT_NOTE",
  RETURNS: "RETURNS",
  WHOLESALE: "WHOLESALE",
  AUDIT_LOG: "AUDIT_LOG",
  REPORTS: "REPORTS",
  EXPENSE: "EXPENSE",
  ACCOUNTING: "ACCOUNTING",
  IMPORT: "IMPORT",
};

export const actionName = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
};

export const actionUrl = {
  // Stores
  STORE_LIST: `/stores`,
  STORE_GET: `/stores/by-id`,
  STORE_CREATE: `/stores`,
  STORE_UPDATE: `/stores`,
  STORE_ACTIVATE: `/stores/activate`,
  STORE_DEACTIVATE: `/stores/deactivate`,
  STORE_CHECK_UNIQUE: `/stores/check-unique`,
  STORE_ADDRESS_LIST: `/stores/addresses`,
  STORE_ADDRESS_CREATE: `/stores/addresses`,
  STORE_ADDRESS_UPDATE: `/stores/addresses`,
  STORE_ADDRESS_DEACTIVATE: `/stores/addresses/deactivate`,

  // Users
  USER_LIST: `/users`,
  USER_GET: `/users/detail`,
  USER_CREATE: `/users`,
  USER_UPDATE: `/users`,
  USER_DELETE: `/users`,
  USER_ACTIVATE: `/users/activate`,
  USER_DEACTIVATE: `/users/deactivate`,
  USER_SUSPEND: `/users/{id}/suspend`,
  USER_UNSUSPEND: `/users/{id}/unsuspend`,
  USER_RESEND_INVITE: `/users/{id}/resend-invite`,
  USER_STORES_GET: `/users/stores`,
  USER_STORES_UPDATE: `/users/stores`,

  // Roles
  ROLE_LIST: `/roles/list`,
  ROLE_CREATE: `/roles`,
  ROLE_UPDATE: `/roles`,
  ROLE_DELETE: `/roles`,
  ROLE_PERMISSIONS_GET: `/roles/permissions`,
  ROLE_PERMISSIONS_UPDATE: `/roles/permissions`,
  STORE_TYPE_PERMISSIONS_GET: `/store-type-permissions`,
  STORE_TYPE_PERMISSIONS_UPDATE: `/store-type-permissions`,

  // POS (sales)
  POS_VIEW: `/sales/expand`,
  POS_CHECKOUT: `/sales/checkout`,
  POS_VOID: `/sales/void`,

  // Inventory (FE-only sidebar group)
  INVENTORY_LIST: `/inventory`,
  INVENTORY_UPDATE: `/inventory`,

  // Transfers (FE-only — Transfer Orders retired)
  TRANSFER_LIST: `/transfers`,
  TRANSFER_CREATE: `/transfers`,

  // Customers
  CUSTOMER_LIST: `/customers`,
  CUSTOMER_GET: `/customers/by-id`,
  CUSTOMER_CREATE: `/customers`,
  CUSTOMER_UPDATE: `/customers`,
  CUSTOMER_DEACTIVATE: `/customers/deactivate`,
  CUSTOMER_REACTIVATE: `/customers/reactivate`,

  // Master types (FE-only)
  MASTER_TYPE_LIST: `/master`,

  // Files (FE-only)
  FILE_UPLOAD: `/files/upload`,
  FILE_PRESIGNED_URL: `/files/presigned-url`,

  // Dashboard (FE-only)
  DASHBOARD_VIEW: `/dashboard`,

  // Settings (FE-only)
  SETTING_VIEW: `/settings`,

  // Tax Settings — HSN library
  HSN_LIST: `/hsn`,
  HSN_CREATE: `/hsn`,
  HSN_UPDATE: `/hsn`,
  HSN_DEACTIVATE: `/hsn/deactivate`,
  HSN_REACTIVATE: `/hsn/reactivate`,

  // Store GST slabs — per-store GST price bands
  STORE_GST_SLAB_LIST: `/store-gst-slabs`,
  STORE_GST_SLAB_CREATE: `/store-gst-slabs`,
  STORE_GST_SLAB_UPDATE: `/store-gst-slabs`,
  STORE_GST_SLAB_DEACTIVATE: `/store-gst-slabs/deactivate`,

  // GST — GSTIN master
  GSTIN_LIST: `/gstins`,
  GSTIN_CREATE: `/gstins`,
  GSTIN_UPDATE: `/gstins`,
  GSTIN_VERIFY: `/gstins/verify`,
  GSTIN_DEACTIVATE: `/gstins/deactivate`,
  GSTIN_REACTIVATE: `/gstins/reactivate`,

  // Invoice Settings
  INVOICE_SETTINGS_GET: `/stores/invoice-settings`,
  INVOICE_SETTINGS_UPDATE: `/stores/invoice-settings`,
  INVOICE_SETTINGS_LOGO_UPLOAD: `/stores/invoice-settings/logo`,
  INVOICE_SETTINGS_LOGO_DELETE: `/stores/invoice-settings/logo`,
  INVOICE_SETTINGS_SEQUENCE_GET: `/stores/invoice-settings/sequence`,
  INVOICE_SETTINGS_SEQUENCE_RESET: `/stores/invoice-settings/sequence/reset`,

  // Profile (FE-only)
  PROFILE_VIEW: `/auth/me`,

  // Inventory Catalogue — Products / Inbound Parents
  PRODUCT_LIST: `/products`,
  PRODUCT_CREATE: `/products`,
  PRODUCT_UPDATE: `/products`,
  PRODUCT_SET_DEFINITION: `/products/set-definition`,
  PRODUCT_DEACTIVATE: `/products/deactivate`,
  PRODUCT_REACTIVATE: `/products/reactivate`,
  PRODUCT_SKU_LIST: `/product-skus`,
  PRODUCT_SKU_CREATE: `/product-skus`,
  PRODUCT_SKU_UPDATE: `/product-skus`,

  INBOUND_PARENT_LIST: `/inbound-parents`,
  // Manual stock CRUD (Importer Rework Phase 5)
  INBOUND_STOCK_ADD: `/inbound-parents`,
  INBOUND_STOCK_UPDATE: `/inbound-parents`,

  // Inventory Stock (Levels/Adjustment/Low Stock Alerts removed V108)
  STOCK_MOVEMENTS_LIST: `/stock-movements`,

  // GRN
  GRN_LIST: `/grn`,
  GRN_GET: `/grn/by-id`,
  GRN_CREATE: `/grn`,
  GRN_UPDATE: `/grn`,
  GRN_SUBMIT: `/grn/submit`,
  GRN_APPROVE: `/grn/approve`,
  GRN_RECEIVE: `/grn/receive`,
  GRN_REJECT: `/grn/reject`,
  GRN_CANCEL: `/grn/cancel`,

  // Registers (POS Billing PRD 1)
  REGISTER_LIST: `/registers`,
  REGISTER_GET: `/registers/by-id`,
  REGISTER_BY_STORE: `/registers/by-store`,
  REGISTER_CHECK_UNIQUE: `/registers/check-unique`,
  REGISTER_CREATE: `/registers`,
  REGISTER_UPDATE: `/registers`,
  REGISTER_ACTIVATE: `/registers/activate`,
  REGISTER_DEACTIVATE: `/registers/deactivate`,

  // Companies (transporter master)
  COMPANY_LIST: `/companies`,
  COMPANY_GET: `/companies/by-id`,
  COMPANY_CHECK_UNIQUE: `/companies/check-unique`,
  COMPANY_CREATE: `/companies`,
  COMPANY_UPDATE: `/companies`,
  COMPANY_DEACTIVATE: `/companies/deactivate`,
  COMPANY_REACTIVATE: `/companies/reactivate`,

  DISCOUNT_LIST: `/discounts`,
  DISCOUNT_GET: `/discounts/by-id`,
  DISCOUNT_CREATE: `/discounts`,
  DISCOUNT_UPDATE: `/discounts`,
  DISCOUNT_DEACTIVATE: `/discounts/deactivate`,
  DISCOUNT_REACTIVATE: `/discounts/reactivate`,
  DISCOUNT_REPORT: `/discounts/report`,
  EXPENSE_CATEGORY_LIST: `/expense-categories`,
  EXPENSE_CATEGORY_GET: `/expense-categories/by-id`,
  EXPENSE_CATEGORY_CREATE: `/expense-categories`,
  EXPENSE_CATEGORY_UPDATE: `/expense-categories`,
  EXPENSE_CATEGORY_DEACTIVATE: `/expense-categories/deactivate`,
  EXPENSE_CATEGORY_REACTIVATE: `/expense-categories/reactivate`,
  EXPENSE_LIST: `/expenses`,
  EXPENSE_GET: `/expenses/by-id`,
  EXPENSE_CREATE: `/expenses`,
  EXPENSE_UPDATE: `/expenses`,
  EXPENSE_DELETE: `/expenses`,
  EXPENSE_APPROVE: `/expenses/approve`,
  EXPENSE_REJECT: `/expenses/reject`,
  MEMBERSHIP_PLAN_LIST: `/membership-plans`,
  MEMBERSHIP_PLAN_GET: `/membership-plans/by-id`,
  MEMBERSHIP_PLAN_CREATE: `/membership-plans`,
  MEMBERSHIP_PLAN_UPDATE: `/membership-plans`,
  MEMBERSHIP_PLAN_DEACTIVATE: `/membership-plans/deactivate`,
  MEMBERSHIP_PLAN_REACTIVATE: `/membership-plans/reactivate`,
  MEMBERSHIP_ACTIVE: `/memberships/active`,
  MEMBERSHIP_REDEEM: `/memberships/redeem`,
  LOYALTY_CONFIG: `/loyalty/config`,
  LOYALTY_BALANCE: `/loyalty/balance`,
  LOYALTY_LEDGER: `/loyalty/ledger`,

  // Reports
  SALES_REPORT: `/reports/sales`,
  STOCK_AGING_REPORT: `/reports/stock-aging`,
  ARTICLE_MOVEMENT: `/reports/article-movement`,

  // Accounting & Financial Reports
  TRADING_PL_REPORT: `/accounting/trading-pl`,
  PURCHASE_SALE_REPORT: `/accounting/purchase-sale`,
  ACCOUNTING_CONFIG: `/accounting/config`,

  // Zoho Inventory Importer (both actions are POST multipart)
  IMPORT_DRY_RUN: `/import/dry-run`,
  IMPORT_COMMIT: `/import/commit`,
  // Importer Rework — global Product import (Item + Composite, no store)
  IMPORT_PRODUCTS_DRY_RUN: `/import/products/dry-run`,
  IMPORT_PRODUCTS_COMMIT: `/import/products/commit`,
  // Importer Rework — per-store Inventory (stock) import (Inventory Summary, nav store)
  IMPORT_STOCK_DRY_RUN: `/import/stock/dry-run`,
  IMPORT_STOCK_COMMIT: `/import/stock/commit`,
  // Own-Template Excel import (products = GET template + POST dry-run/commit; stock likewise)
  IMPORT_PRODUCTS_TEMPLATE: `/import/products/template`,
  IMPORT_PRODUCTS_TEMPLATE_DRY_RUN: `/import/products/template/dry-run`,
  IMPORT_PRODUCTS_TEMPLATE_COMMIT: `/import/products/template/commit`,
  IMPORT_STOCK_TEMPLATE: `/import/stock/template`,
  IMPORT_STOCK_TEMPLATE_DRY_RUN: `/import/stock/template/dry-run`,
  IMPORT_STOCK_TEMPLATE_COMMIT: `/import/stock/template/commit`,

  // Credit Notes
  CREDIT_NOTE_LIST: `/credit-notes`,
  CREDIT_NOTE_GET: `/credit-notes/by-id`,
  CREDIT_NOTE_CREATE: `/credit-notes`,
  CREDIT_NOTE_CANCEL: `/credit-notes/cancel`,

  // Returns
  RETURN_LIST: `/returns`,
  RETURN_GET: `/returns/by-id`,
  RETURNABLE_BILL: `/returns/returnable-bill`,
  RETURN_CREATE: `/returns`,
  RETURN_APPROVE: `/returns/approve`,
  RETURN_REJECT: `/returns/reject`,

  // Multi-Challan (wholesale dispatch)
  CHALLAN_LIST: `/multi-challan`,
  CHALLAN_GET: `/multi-challan/by-id`,
  CHALLAN_CREATE: `/multi-challan`,
  CHALLAN_UPDATE: `/multi-challan`,
  CHALLAN_DELETE: `/multi-challan`,
  CHALLAN_DISPATCH: `/multi-challan/dispatch`,
  CHALLAN_GENERATE_EWAYBILL: `/multi-challan/generate-ewaybill`,

  // Multi-Invoice (wholesale GST tax invoice)
  INVOICE_LIST: `/multi-invoice`,
  INVOICE_GET: `/multi-invoice/by-id`,
  INVOICE_CREATE: `/multi-invoice`,
  INVOICE_UPDATE: `/multi-invoice`,
  INVOICE_DELETE: `/multi-invoice`,
  INVOICE_GENERATE_IRN: `/multi-invoice/generate-irn`,
  INVOICE_GENERATE_EWAYBILL: `/multi-invoice/generate-ewaybill`,
  INVOICE_PT_FILE: `/multi-invoice/pt-file`,

  // Audit Log
  AUDIT_LOG_LIST: `/audit-logs`,
  AUDIT_LOG_EXPORT: `/audit-logs/export`,
};

// Demo permission seeds for the in-memory auth provider.
// Replace with API response on real backend wiring (PRD Phase 5).
const buildPermission = (module, method, url, allowed = true) => ({
  allowed,
  method,
  actionUrl: url,
  modules: { name: module },
});

export const SUPER_ADMIN_PERMISSIONS = [
  // Stores — full
  buildPermission(moduleName.STORE, actionName.GET, actionUrl.STORE_LIST),
  buildPermission(moduleName.STORE, actionName.GET, actionUrl.STORE_GET),
  buildPermission(moduleName.STORE, actionName.POST, actionUrl.STORE_CREATE),
  buildPermission(moduleName.STORE, actionName.PATCH, actionUrl.STORE_UPDATE),
  buildPermission(moduleName.STORE, actionName.POST, actionUrl.STORE_ACTIVATE),
  buildPermission(moduleName.STORE, actionName.POST, actionUrl.STORE_DEACTIVATE),
  buildPermission(moduleName.STORE, actionName.GET, actionUrl.STORE_ADDRESS_LIST),
  buildPermission(moduleName.STORE, actionName.POST, actionUrl.STORE_ADDRESS_CREATE),
  buildPermission(moduleName.STORE, actionName.PUT, actionUrl.STORE_ADDRESS_UPDATE),
  buildPermission(moduleName.STORE, actionName.PATCH, actionUrl.STORE_ADDRESS_DEACTIVATE),
  buildPermission(moduleName.STORE, actionName.GET, actionUrl.STORE_CHECK_UNIQUE),

  // Users + Roles
  buildPermission(moduleName.USER, actionName.GET, actionUrl.USER_LIST),
  buildPermission(moduleName.USER, actionName.POST, actionUrl.USER_CREATE),
  buildPermission(moduleName.USER, actionName.PUT, actionUrl.USER_UPDATE),
  buildPermission(moduleName.USER, actionName.DELETE, actionUrl.USER_DELETE),
  buildPermission(moduleName.USER, actionName.POST, actionUrl.USER_ACTIVATE),
  buildPermission(moduleName.USER, actionName.POST, actionUrl.USER_DEACTIVATE),
  buildPermission(moduleName.USER, actionName.GET, actionUrl.USER_GET),
  buildPermission(moduleName.USER, actionName.POST, actionUrl.USER_SUSPEND),
  buildPermission(moduleName.USER, actionName.POST, actionUrl.USER_UNSUSPEND),
  buildPermission(moduleName.USER, actionName.POST, actionUrl.USER_RESEND_INVITE),
  buildPermission(moduleName.USER, actionName.GET, actionUrl.USER_STORES_GET),
  buildPermission(moduleName.USER, actionName.PUT, actionUrl.USER_STORES_UPDATE),
  buildPermission(moduleName.ROLE, actionName.GET, actionUrl.ROLE_LIST),
  buildPermission(moduleName.ROLE, actionName.POST, actionUrl.ROLE_CREATE),
  buildPermission(moduleName.ROLE, actionName.PUT, actionUrl.ROLE_UPDATE),
  buildPermission(moduleName.ROLE, actionName.DELETE, actionUrl.ROLE_DELETE),
  buildPermission(moduleName.ROLE, actionName.GET, actionUrl.ROLE_PERMISSIONS_GET),
  buildPermission(moduleName.ROLE, actionName.PUT, actionUrl.ROLE_PERMISSIONS_UPDATE),
  buildPermission(moduleName.ROLE, actionName.GET, actionUrl.STORE_TYPE_PERMISSIONS_GET),
  buildPermission(moduleName.ROLE, actionName.PUT, actionUrl.STORE_TYPE_PERMISSIONS_UPDATE),

  // POS
  buildPermission(moduleName.POS, actionName.GET, actionUrl.POS_VIEW),
  buildPermission(moduleName.POS, actionName.POST, actionUrl.POS_CHECKOUT),
  buildPermission(moduleName.POS, actionName.POST, actionUrl.POS_VOID),

  // Inventory / Transfers (FE-only groupings)
  buildPermission(moduleName.INVENTORY, actionName.GET, actionUrl.INVENTORY_LIST),
  buildPermission(moduleName.TRANSFER, actionName.GET, actionUrl.TRANSFER_LIST),

  // GRN
  buildPermission(moduleName.GRN, actionName.GET, actionUrl.GRN_LIST),
  buildPermission(moduleName.GRN, actionName.GET, actionUrl.GRN_GET),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_CREATE),
  buildPermission(moduleName.GRN, actionName.PUT, actionUrl.GRN_UPDATE),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_SUBMIT),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_APPROVE),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_RECEIVE),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_REJECT),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_CANCEL),

  // Registers
  buildPermission(moduleName.REGISTER, actionName.GET, actionUrl.REGISTER_LIST),
  buildPermission(moduleName.REGISTER, actionName.GET, actionUrl.REGISTER_GET),
  buildPermission(moduleName.REGISTER, actionName.POST, actionUrl.REGISTER_CREATE),
  buildPermission(moduleName.REGISTER, actionName.PUT, actionUrl.REGISTER_UPDATE),
  buildPermission(moduleName.REGISTER, actionName.PATCH, actionUrl.REGISTER_ACTIVATE),
  buildPermission(moduleName.REGISTER, actionName.PATCH, actionUrl.REGISTER_DEACTIVATE),

  // Companies
  buildPermission(moduleName.COMPANY, actionName.GET, actionUrl.COMPANY_LIST),
  buildPermission(moduleName.COMPANY, actionName.GET, actionUrl.COMPANY_GET),
  buildPermission(moduleName.COMPANY, actionName.POST, actionUrl.COMPANY_CREATE),
  buildPermission(moduleName.COMPANY, actionName.PUT, actionUrl.COMPANY_UPDATE),
  buildPermission(moduleName.COMPANY, actionName.PATCH, actionUrl.COMPANY_DEACTIVATE),
  buildPermission(moduleName.COMPANY, actionName.PATCH, actionUrl.COMPANY_REACTIVATE),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_CATEGORY_LIST),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_CATEGORY_GET),
  buildPermission(moduleName.EXPENSE, actionName.POST, actionUrl.EXPENSE_CATEGORY_CREATE),
  buildPermission(moduleName.EXPENSE, actionName.PUT, actionUrl.EXPENSE_CATEGORY_UPDATE),
  buildPermission(moduleName.EXPENSE, actionName.PATCH, actionUrl.EXPENSE_CATEGORY_DEACTIVATE),
  buildPermission(moduleName.EXPENSE, actionName.PATCH, actionUrl.EXPENSE_CATEGORY_REACTIVATE),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_LIST),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_GET),
  buildPermission(moduleName.EXPENSE, actionName.POST, actionUrl.EXPENSE_CREATE),
  buildPermission(moduleName.EXPENSE, actionName.PUT, actionUrl.EXPENSE_UPDATE),
  buildPermission(moduleName.EXPENSE, actionName.DELETE, actionUrl.EXPENSE_DELETE),
  buildPermission(moduleName.EXPENSE, actionName.PATCH, actionUrl.EXPENSE_APPROVE),
  buildPermission(moduleName.EXPENSE, actionName.PATCH, actionUrl.EXPENSE_REJECT),
  buildPermission(moduleName.DISCOUNT, actionName.GET, actionUrl.DISCOUNT_LIST),
  buildPermission(moduleName.DISCOUNT, actionName.GET, actionUrl.DISCOUNT_GET),
  buildPermission(moduleName.DISCOUNT, actionName.POST, actionUrl.DISCOUNT_CREATE),
  buildPermission(moduleName.DISCOUNT, actionName.PUT, actionUrl.DISCOUNT_UPDATE),
  buildPermission(moduleName.DISCOUNT, actionName.PATCH, actionUrl.DISCOUNT_DEACTIVATE),
  buildPermission(moduleName.DISCOUNT, actionName.PATCH, actionUrl.DISCOUNT_REACTIVATE),
  buildPermission(moduleName.DISCOUNT, actionName.GET, actionUrl.DISCOUNT_REPORT),

  // Memberships
  buildPermission(moduleName.MEMBERSHIP, actionName.GET, actionUrl.MEMBERSHIP_PLAN_LIST),
  buildPermission(moduleName.MEMBERSHIP, actionName.GET, actionUrl.MEMBERSHIP_PLAN_GET),
  buildPermission(moduleName.MEMBERSHIP, actionName.POST, actionUrl.MEMBERSHIP_PLAN_CREATE),
  buildPermission(moduleName.MEMBERSHIP, actionName.PUT, actionUrl.MEMBERSHIP_PLAN_UPDATE),
  buildPermission(moduleName.MEMBERSHIP, actionName.PATCH, actionUrl.MEMBERSHIP_PLAN_DEACTIVATE),
  buildPermission(moduleName.MEMBERSHIP, actionName.PATCH, actionUrl.MEMBERSHIP_PLAN_REACTIVATE),
  buildPermission(moduleName.MEMBERSHIP, actionName.GET, actionUrl.MEMBERSHIP_ACTIVE),
  buildPermission(moduleName.MEMBERSHIP, actionName.POST, actionUrl.MEMBERSHIP_REDEEM),
  buildPermission(moduleName.LOYALTY, actionName.GET, actionUrl.LOYALTY_CONFIG),
  buildPermission(moduleName.LOYALTY, actionName.PUT, actionUrl.LOYALTY_CONFIG),
  buildPermission(moduleName.LOYALTY, actionName.GET, actionUrl.LOYALTY_BALANCE),
  buildPermission(moduleName.LOYALTY, actionName.GET, actionUrl.LOYALTY_LEDGER),

  // Wholesale — challans + invoices
  buildPermission(moduleName.WHOLESALE, actionName.GET, actionUrl.CHALLAN_LIST),
  buildPermission(moduleName.WHOLESALE, actionName.GET, actionUrl.CHALLAN_GET),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.CHALLAN_CREATE),
  buildPermission(moduleName.WHOLESALE, actionName.PUT, actionUrl.CHALLAN_UPDATE),
  buildPermission(moduleName.WHOLESALE, actionName.DELETE, actionUrl.CHALLAN_DELETE),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.CHALLAN_DISPATCH),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.CHALLAN_GENERATE_EWAYBILL),
  buildPermission(moduleName.WHOLESALE, actionName.GET, actionUrl.INVOICE_LIST),
  buildPermission(moduleName.WHOLESALE, actionName.GET, actionUrl.INVOICE_GET),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.INVOICE_CREATE),
  buildPermission(moduleName.WHOLESALE, actionName.PUT, actionUrl.INVOICE_UPDATE),
  buildPermission(moduleName.WHOLESALE, actionName.DELETE, actionUrl.INVOICE_DELETE),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.INVOICE_GENERATE_IRN),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.INVOICE_GENERATE_EWAYBILL),
  buildPermission(moduleName.WHOLESALE, actionName.GET, actionUrl.INVOICE_PT_FILE),
  buildPermission(moduleName.WHOLESALE, actionName.POST, actionUrl.INVOICE_PT_FILE),

  // Credit Notes
  buildPermission(moduleName.CREDIT_NOTE, actionName.GET, actionUrl.CREDIT_NOTE_LIST),
  buildPermission(moduleName.CREDIT_NOTE, actionName.GET, actionUrl.CREDIT_NOTE_GET),
  buildPermission(moduleName.CREDIT_NOTE, actionName.POST, actionUrl.CREDIT_NOTE_CREATE),
  buildPermission(moduleName.CREDIT_NOTE, actionName.PATCH, actionUrl.CREDIT_NOTE_CANCEL),

  // Returns
  buildPermission(moduleName.RETURNS, actionName.GET, actionUrl.RETURN_LIST),
  buildPermission(moduleName.RETURNS, actionName.GET, actionUrl.RETURN_GET),
  buildPermission(moduleName.RETURNS, actionName.GET, actionUrl.RETURNABLE_BILL),
  buildPermission(moduleName.RETURNS, actionName.POST, actionUrl.RETURN_CREATE),
  buildPermission(moduleName.RETURNS, actionName.PATCH, actionUrl.RETURN_APPROVE),
  buildPermission(moduleName.RETURNS, actionName.PATCH, actionUrl.RETURN_REJECT),

  // Customers / Settings / Dashboard / Profile
  buildPermission(moduleName.CUSTOMER, actionName.GET, actionUrl.CUSTOMER_LIST),
  buildPermission(moduleName.CUSTOMER, actionName.GET, actionUrl.CUSTOMER_GET),
  buildPermission(moduleName.CUSTOMER, actionName.POST, actionUrl.CUSTOMER_CREATE),
  buildPermission(moduleName.CUSTOMER, actionName.PUT, actionUrl.CUSTOMER_UPDATE),
  buildPermission(moduleName.CUSTOMER, actionName.POST, actionUrl.CUSTOMER_DEACTIVATE),
  buildPermission(moduleName.CUSTOMER, actionName.POST, actionUrl.CUSTOMER_REACTIVATE),
  buildPermission(moduleName.SETTING, actionName.GET, actionUrl.SETTING_VIEW),
  buildPermission(moduleName.DASHBOARD, actionName.GET, actionUrl.DASHBOARD_VIEW),
  buildPermission(moduleName.PROFILE, actionName.GET, actionUrl.PROFILE_VIEW),

  // Master + Files (FE-only)
  buildPermission(moduleName.MASTER_TYPE, actionName.GET, actionUrl.MASTER_TYPE_LIST),
  buildPermission(moduleName.FILE, actionName.POST, actionUrl.FILE_UPLOAD),

  // Tax Settings — HSN library
  buildPermission(moduleName.TAX_SETTINGS, actionName.GET, actionUrl.HSN_LIST),
  buildPermission(moduleName.TAX_SETTINGS, actionName.POST, actionUrl.HSN_CREATE),
  buildPermission(moduleName.TAX_SETTINGS, actionName.PUT, actionUrl.HSN_UPDATE),
  buildPermission(moduleName.TAX_SETTINGS, actionName.POST, actionUrl.HSN_DEACTIVATE),
  buildPermission(moduleName.TAX_SETTINGS, actionName.POST, actionUrl.HSN_REACTIVATE),

  // Store GST slabs
  buildPermission(moduleName.STORE_GST_SLABS, actionName.GET, actionUrl.STORE_GST_SLAB_LIST),
  buildPermission(moduleName.STORE_GST_SLABS, actionName.POST, actionUrl.STORE_GST_SLAB_CREATE),
  buildPermission(moduleName.STORE_GST_SLABS, actionName.PUT, actionUrl.STORE_GST_SLAB_UPDATE),
  buildPermission(
    moduleName.STORE_GST_SLABS,
    actionName.PATCH,
    actionUrl.STORE_GST_SLAB_DEACTIVATE,
  ),

  // GST — GSTIN master
  buildPermission(moduleName.GST, actionName.GET, actionUrl.GSTIN_LIST),
  buildPermission(moduleName.GST, actionName.POST, actionUrl.GSTIN_CREATE),
  buildPermission(moduleName.GST, actionName.PUT, actionUrl.GSTIN_UPDATE),
  buildPermission(moduleName.GST, actionName.POST, actionUrl.GSTIN_VERIFY),
  buildPermission(moduleName.GST, actionName.POST, actionUrl.GSTIN_DEACTIVATE),
  buildPermission(moduleName.GST, actionName.POST, actionUrl.GSTIN_REACTIVATE),

  // Invoice Settings
  buildPermission(moduleName.INVOICE_SETTINGS, actionName.GET, actionUrl.INVOICE_SETTINGS_GET),
  buildPermission(moduleName.INVOICE_SETTINGS, actionName.PUT, actionUrl.INVOICE_SETTINGS_UPDATE),
  buildPermission(
    moduleName.INVOICE_SETTINGS,
    actionName.POST,
    actionUrl.INVOICE_SETTINGS_LOGO_UPLOAD,
  ),
  buildPermission(
    moduleName.INVOICE_SETTINGS,
    actionName.DELETE,
    actionUrl.INVOICE_SETTINGS_LOGO_DELETE,
  ),
  buildPermission(
    moduleName.INVOICE_SETTINGS,
    actionName.GET,
    actionUrl.INVOICE_SETTINGS_SEQUENCE_GET,
  ),
  buildPermission(
    moduleName.INVOICE_SETTINGS,
    actionName.POST,
    actionUrl.INVOICE_SETTINGS_SEQUENCE_RESET,
  ),

  // Inventory Catalogue — Products / Inbound Parents
  buildPermission(moduleName.PRODUCTS, actionName.GET, actionUrl.PRODUCT_LIST),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_CREATE),
  buildPermission(moduleName.PRODUCTS, actionName.PUT, actionUrl.PRODUCT_UPDATE),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_SET_DEFINITION),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_DEACTIVATE),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_REACTIVATE),
  buildPermission(moduleName.PRODUCTS, actionName.GET, actionUrl.PRODUCT_SKU_LIST),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_SKU_CREATE),
  buildPermission(moduleName.PRODUCTS, actionName.PUT, actionUrl.PRODUCT_SKU_UPDATE),
  buildPermission(moduleName.PRODUCTS, actionName.GET, actionUrl.INBOUND_PARENT_LIST),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.INBOUND_STOCK_ADD),
  buildPermission(moduleName.PRODUCTS, actionName.PUT, actionUrl.INBOUND_STOCK_UPDATE),

  // Inventory Stock (Levels/Adjustment/Low Stock Alerts removed V108)
  buildPermission(moduleName.STOCK, actionName.GET, actionUrl.STOCK_MOVEMENTS_LIST),

  // Audit Log
  buildPermission(moduleName.AUDIT_LOG, actionName.GET, actionUrl.AUDIT_LOG_LIST),
  buildPermission(moduleName.AUDIT_LOG, actionName.GET, actionUrl.AUDIT_LOG_EXPORT),

  // Own-Template Excel import (SUPER_ADMIN + HO_MANAGER get all six)
  buildPermission(moduleName.IMPORT, actionName.GET, actionUrl.IMPORT_PRODUCTS_TEMPLATE),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_PRODUCTS_TEMPLATE_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_PRODUCTS_TEMPLATE_COMMIT),
  buildPermission(moduleName.IMPORT, actionName.GET, actionUrl.IMPORT_STOCK_TEMPLATE),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_TEMPLATE_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_TEMPLATE_COMMIT),
];

// Store Manager — POS + day-to-day ops, no Stores/Users CRUD
export const STORE_MANAGER_PERMISSIONS = [
  buildPermission(moduleName.DASHBOARD, actionName.GET, actionUrl.DASHBOARD_VIEW),
  buildPermission(moduleName.POS, actionName.GET, actionUrl.POS_VIEW),
  buildPermission(moduleName.POS, actionName.POST, actionUrl.POS_CHECKOUT),
  buildPermission(moduleName.POS, actionName.POST, actionUrl.POS_VOID),
  buildPermission(moduleName.INVENTORY, actionName.GET, actionUrl.INVENTORY_LIST),
  buildPermission(moduleName.GRN, actionName.GET, actionUrl.GRN_LIST),
  buildPermission(moduleName.GRN, actionName.GET, actionUrl.GRN_GET),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_CREATE),
  buildPermission(moduleName.GRN, actionName.PUT, actionUrl.GRN_UPDATE),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_SUBMIT),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_APPROVE),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_RECEIVE),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_REJECT),
  buildPermission(moduleName.GRN, actionName.POST, actionUrl.GRN_CANCEL),
  buildPermission(moduleName.CUSTOMER, actionName.GET, actionUrl.CUSTOMER_LIST),
  buildPermission(moduleName.CUSTOMER, actionName.POST, actionUrl.CUSTOMER_CREATE),
  buildPermission(moduleName.CUSTOMER, actionName.PUT, actionUrl.CUSTOMER_UPDATE),
  buildPermission(moduleName.STORE, actionName.GET, actionUrl.STORE_LIST),
  buildPermission(moduleName.PROFILE, actionName.GET, actionUrl.PROFILE_VIEW),
  buildPermission(moduleName.MASTER_TYPE, actionName.GET, actionUrl.MASTER_TYPE_LIST),

  // Inventory Catalogue — Products (read + soft-delete; manual authoring is SA/HO only)
  buildPermission(moduleName.PRODUCTS, actionName.GET, actionUrl.PRODUCT_LIST),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_DEACTIVATE),
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.PRODUCT_REACTIVATE),
  buildPermission(moduleName.PRODUCTS, actionName.GET, actionUrl.PRODUCT_SKU_LIST),
  buildPermission(moduleName.PRODUCTS, actionName.GET, actionUrl.INBOUND_PARENT_LIST),
  // Manual per-store stock CRUD (Importer Rework Phase 5) — SM manages own store
  buildPermission(moduleName.PRODUCTS, actionName.POST, actionUrl.INBOUND_STOCK_ADD),
  buildPermission(moduleName.PRODUCTS, actionName.PUT, actionUrl.INBOUND_STOCK_UPDATE),

  // Per-store Inventory (stock) import — own active store (Importer Rework, Phase 3)
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_COMMIT),
  // Own-Template Excel stock import — own active store
  buildPermission(moduleName.IMPORT, actionName.GET, actionUrl.IMPORT_STOCK_TEMPLATE),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_TEMPLATE_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_TEMPLATE_COMMIT),

  // Inventory Stock — movements only (Levels/Adjustment/Low Stock Alerts removed V108)
  buildPermission(moduleName.STOCK, actionName.GET, actionUrl.STOCK_MOVEMENTS_LIST),

  // Credit Notes — issue / view / cancel
  buildPermission(moduleName.CREDIT_NOTE, actionName.GET, actionUrl.CREDIT_NOTE_LIST),
  buildPermission(moduleName.CREDIT_NOTE, actionName.GET, actionUrl.CREDIT_NOTE_GET),
  buildPermission(moduleName.CREDIT_NOTE, actionName.POST, actionUrl.CREDIT_NOTE_CREATE),
  buildPermission(moduleName.CREDIT_NOTE, actionName.PATCH, actionUrl.CREDIT_NOTE_CANCEL),

  // Returns
  buildPermission(moduleName.RETURNS, actionName.GET, actionUrl.RETURN_LIST),
  buildPermission(moduleName.RETURNS, actionName.GET, actionUrl.RETURN_GET),
  buildPermission(moduleName.RETURNS, actionName.GET, actionUrl.RETURNABLE_BILL),
  buildPermission(moduleName.RETURNS, actionName.POST, actionUrl.RETURN_CREATE),

  // Reports
  buildPermission(moduleName.REPORTS, actionName.GET, actionUrl.SALES_REPORT),
  buildPermission(moduleName.REPORTS, actionName.GET, actionUrl.STOCK_AGING_REPORT),
  buildPermission(moduleName.REPORTS, actionName.GET, actionUrl.ARTICLE_MOVEMENT),

  // Accounting & Financial Reports
  buildPermission(moduleName.ACCOUNTING, actionName.GET, actionUrl.TRADING_PL_REPORT),
  buildPermission(moduleName.ACCOUNTING, actionName.GET, actionUrl.PURCHASE_SALE_REPORT),
  buildPermission(moduleName.ACCOUNTING, actionName.GET, actionUrl.ACCOUNTING_CONFIG),
  buildPermission(moduleName.ACCOUNTING, actionName.PUT, actionUrl.ACCOUNTING_CONFIG),

  // Zoho Inventory Importer (SUPER_ADMIN + HO_MANAGER only)
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_COMMIT),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_PRODUCTS_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_PRODUCTS_COMMIT),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_DRY_RUN),
  buildPermission(moduleName.IMPORT, actionName.POST, actionUrl.IMPORT_STOCK_COMMIT),

  // Expenses (Store Manager files + reads; category read for the picker)
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_CATEGORY_LIST),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_CATEGORY_GET),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_LIST),
  buildPermission(moduleName.EXPENSE, actionName.GET, actionUrl.EXPENSE_GET),
  buildPermission(moduleName.EXPENSE, actionName.POST, actionUrl.EXPENSE_CREATE),
  buildPermission(moduleName.EXPENSE, actionName.PUT, actionUrl.EXPENSE_UPDATE),
  buildPermission(moduleName.EXPENSE, actionName.DELETE, actionUrl.EXPENSE_DELETE),
];
